import { n as prepareMetadata } from "../util-ugZPQ7Ug.js";
//#region src/transports/sentry.ts
const sentrySeverityMap = {
	["debug"]: "debug",
	["info"]: "info",
	["log"]: "log",
	["warn"]: "warning",
	["error"]: "error"
};
const cleanupFunctions = [];
function isLogAttr(value) {
	return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}
function buildLogAttrs(context, metadata) {
	const attrs = {};
	const nested = metadata.metadata;
	if (nested && typeof nested === "object" && !Array.isArray(nested)) {
		for (const [k, v] of Object.entries(nested)) if (isLogAttr(v)) attrs[k] = v;
	}
	attrs.context = context ?? "unknown";
	for (const [k, v] of Object.entries(metadata)) {
		if (k === "type" || k === "tags" || k === "__context__") continue;
		if (isLogAttr(v)) attrs[k] = v;
	}
	return attrs;
}
function mergeTags(attrs, tags) {
	if (!tags) return attrs;
	for (const [k, v] of Object.entries(tags)) if (v !== null && v !== void 0 && (typeof v === "string" || typeof v === "number" || typeof v === "boolean")) attrs[`tag.${k}`] = v;
	return attrs;
}
function normalizeCaptureContext(captureContext) {
	if (typeof captureContext === "string") return { level: captureContext };
	if (captureContext && typeof captureContext === "object") {
		const ctx = captureContext;
		const result = {};
		if (ctx.level) result.level = ctx.level;
		if (ctx.tags) result.tags = ctx.tags;
		if (ctx.extra) result.extra = { ...ctx.extra };
		if (ctx.fingerprint) result.fingerprint = ctx.fingerprint;
		if (ctx.exception) result.exception = ctx.exception;
		return result;
	}
	return {};
}
function handleObjectMessage(message, meta, tags, sentry) {
	let messageStr;
	let stringifyError = null;
	if (typeof message === "object" && message !== null) try {
		messageStr = JSON.stringify(message, null, 2);
	} catch (err) {
		stringifyError = err;
		messageStr = "[object could not be stringified]";
	}
	else messageStr = String(message);
	const extra = {
		...meta,
		originalObject: message,
		...stringifyError ? { stringifyError } : {}
	};
	sentry.captureMessage(messageStr, {
		level: "error",
		tags,
		extra
	});
}
function captureQueuedMessage(sentry, message, context) {
	if (context.exception) {
		sentry.captureException(context.exception, {
			level: context.level,
			tags: {
				...context.tags,
				errorName: context.exception.name || "Error",
				errorConstructor: context.exception.constructor?.name || "Error"
			},
			extra: context.extra,
			fingerprint: context.fingerprint
		});
		return;
	}
	sentry.captureMessage(message, {
		level: context.level,
		tags: context.tags,
		extra: context.extra,
		fingerprint: context.fingerprint
	});
}
function buildQueuedFingerprint(message, context) {
	const originContext = context.extra?.__context__;
	const contextTag = typeof originContext === "string" ? originContext : void 0;
	if (context.exception) {
		const fingerprint = ["{{ default }}"];
		if (contextTag) fingerprint.push(contextTag);
		return fingerprint;
	}
	return contextTag ? [message, contextTag] : [message];
}
function createQueuedMessageHandler(sentry, queueTimeout, captureStacktrace) {
	const queuedMessages = [];
	let sentrySendTimeout = null;
	function sendQueuedMessages() {
		while (queuedMessages.length > 0) {
			const record = queuedMessages.shift();
			if (!record) continue;
			captureQueuedMessage(sentry, record[0], record[1]);
		}
	}
	function queueMessage(message, captureContext) {
		const context = normalizeCaptureContext(captureContext);
		if (captureStacktrace) context.extra = {
			...context.extra,
			stacktrace: captureStacktrace()
		};
		if (!context.fingerprint) context.fingerprint = buildQueuedFingerprint(message, context);
		queuedMessages.push([message, context]);
		if (!sentrySendTimeout) sentrySendTimeout = setTimeout(() => {
			sentrySendTimeout = null;
			sendQueuedMessages();
		}, queueTimeout);
	}
	function flush() {
		if (sentrySendTimeout) {
			clearTimeout(sentrySendTimeout);
			sentrySendTimeout = null;
		}
		sendQueuedMessages();
		if (sentry.flush) sentry.flush();
	}
	return {
		queueMessage,
		flush
	};
}
function handleStringMessage(message, level, context, meta, tags, type, fingerprint, error, timestamp, isNetworkLog, queueMessage, sentry) {
	const severity = sentrySeverityMap[level];
	sentry.addBreadcrumb({
		category: context,
		message,
		data: meta,
		type: type || "default",
		level: severity,
		timestamp: timestamp / 1e3
	});
	if (isNetworkLog && isNetworkLog({
		message,
		extra: meta
	})) return;
	if (level === "error") queueMessage(message, {
		level: severity,
		tags,
		extra: meta,
		fingerprint,
		exception: error
	});
}
function createEmitSentryLog(sentry, isNetworkLog) {
	return function emitSentryLog(level, context, message, metadata, tags) {
		if (level !== "warn") return;
		const logMessage = message instanceof Error ? message.message : String(message);
		if (isNetworkLog && isNetworkLog({
			message: logMessage,
			extra: metadata
		})) return;
		sentry.logger.warn(logMessage, mergeTags(buildLogAttrs(context, metadata), tags));
	};
}
function createTransportBody(sentry, emitSentryLog, queueMessage, isNetworkLog) {
	return (level, context, message, { type, tags, fingerprint, ...metadata }, timestamp) => {
		if (level === "debug") return;
		emitSentryLog(level, context, message, metadata, tags);
		const meta = {
			__context__: context,
			...prepareMetadata(metadata)
		};
		const _tags = {
			category: context,
			...tags
		};
		if (typeof message === "string") handleStringMessage(message, level, context, meta, _tags, type, fingerprint, metadata.error instanceof Error ? metadata.error : void 0, timestamp, isNetworkLog, queueMessage, sentry);
		else if (message instanceof Error) sentry.captureException(message, {
			tags: _tags,
			extra: meta
		});
		else handleObjectMessage(message, meta, _tags, sentry);
	};
}
function createSentryTransport(options) {
	const { sentry, queueTimeout = 7e3, captureStacktrace, onBackgroundFlush, isNetworkLog } = options;
	const emitSentryLog = createEmitSentryLog(sentry, isNetworkLog);
	const { queueMessage, flush } = createQueuedMessageHandler(sentry, queueTimeout, captureStacktrace);
	if (onBackgroundFlush) {
		const cleanup = onBackgroundFlush(flush);
		if (cleanup) cleanupFunctions.push(cleanup);
	}
	return createTransportBody(sentry, emitSentryLog, queueMessage, isNetworkLog);
}
function createSentryFlushHandle(_transport) {}
//#endregion
export { createSentryFlushHandle, createSentryTransport };

//# sourceMappingURL=sentry.js.map